import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import { Radar, RefreshCw, Loader2, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { t } from "@/i18n";

const OT_BADGE = {
  OPPORTUNITY: { icon: TrendingUp, cls: "bg-pos-green/10 text-pos-green border-pos-green/40", key: "market.ot_opportunity" },
  THREAT:      { icon: TrendingDown, cls: "bg-neg-red/10 text-neg-red border-neg-red/40",     key: "market.ot_threat" },
  NEUTRAL:     { icon: Minus, cls: "bg-bg-panel-raised text-text-primary/85 border-border-hairline", key: "market.ot_neutral" },
};
const CONF = {
  HIGH: "bg-pos-green/20 text-pos-green",
  MEDIUM: "bg-warn-amber/20 text-warn-amber",
  LOW: "bg-bg-panel-raised text-text-muted",
};

export default function MarketRadarPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);

  const load = async (refresh = false) => {
    (refresh ? setRefreshing : setLoading)(true);
    setError(null);
    try {
      const { data } = await api.get(`/market-radar${refresh ? "?refresh=true" : ""}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };
  useEffect(() => { load(false); }, []);

  if (loading) return <AppLayout><div className="py-24 text-center text-text-muted"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</div></AppLayout>;

  const signals = data?.signals || [];
  const emptyReason = data?.empty_reason;

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Radar className="w-6 h-6 text-accent-teal" />
            <div>
              <h1 className="text-2xl font-bold text-text-primary tracking-tight" data-testid="market-radar-title">
                {t("market.title")}
              </h1>
              <p className="text-sm text-text-muted mt-0.5">
                {t("market.subtitle")}
                {data?.generated_at && (
                  <> {t("market.last_updated")}: <span>{new Date(data.generated_at).toLocaleString("nl-NL")}</span>.</>
                )}
              </p>
            </div>
          </div>
          <button
            data-testid="market-radar-refresh"
            disabled={refreshing}
            onClick={() => load(true)}
            className="h-10 px-4 rounded-[10px] bg-accent-teal hover:bg-accent-teal-dim disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2"
            style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? t("market.refreshing") : t("market.refresh")}
          </button>
        </div>
      )}
      {hideInnerHeader && (
        <div className="mb-4 flex justify-end">
          <button
            data-testid="market-radar-refresh"
            disabled={refreshing}
            onClick={() => load(true)}
            className="h-9 px-4 rounded-[10px] bg-accent-teal hover:bg-accent-teal-dim disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2"
            style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? t("market.refreshing") : t("market.refresh")}
          </button>
        </div>
      )}

      {data?.fallback_mode && (
        <div data-testid="market-radar-fallback" className="mb-4 p-3 rounded-md border border-warn-amber/40 bg-warn-amber/10 text-warn-amber text-sm">
          {data.fallback_notice_nl || t("market.fallback_notice")}
        </div>
      )}
      {data?.rate_limited && (
        <div data-testid="market-radar-rate-limited" className="mb-4 p-3 rounded-md border border-warn-amber/40 bg-warn-amber/10 text-warn-amber text-sm">
          {t("market.rate_limited")}
        </div>
      )}
      {error && <div className="mb-4 p-3 rounded-md border border-neg-red/40 bg-neg-red/10 text-neg-red text-sm">{error}</div>}

      {signals.length === 0 ? (
        <div data-testid="market-radar-empty" className="rounded-xl border border-dashed border-border-hairline bg-bg-panel p-10 text-center">
          <Radar className="w-8 h-8 text-text-subtle mx-auto mb-3" />
          <p className="text-text-muted">
            {emptyReason === "no_business_profile"
              ? t("market.empty_no_profile")
              : emptyReason === "sources_unreachable"
                ? t("market.empty_unreachable")
                : emptyReason === "no_relevant_articles"
                  ? t("market.empty_no_relevant")
                  : t("market.empty_default")}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="market-radar-list">
          {signals.map((s, i) => {
            const ot = OT_BADGE[s.opportunity_or_threat] || OT_BADGE.NEUTRAL;
            const OtIcon = ot.icon;
            const isOpen = openIdx === i;
            // M147: defensieve key — backend kan dubbele signal-id's leveren
            // waardoor React 'duplicate key'-warnings ontstonden.
            const signalKey = s.signal_id ? `${s.signal_id}-${i}` : `sig-${i}`;
            return (
              <div key={signalKey} data-testid={`market-signal-${i}`}
                   className="rounded-xl border border-border-hairline bg-bg-panel overflow-hidden">
                <button
                  data-testid={`market-signal-toggle-${i}`}
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full p-4 flex items-center gap-3 text-left hover:bg-bg-panel transition-colors"
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-text-subtle" /> : <ChevronRight className="w-4 h-4 text-text-subtle" />}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${ot.cls} flex items-center gap-1`}>
                    <OtIcon className="w-3 h-3" /> {t(ot.key)}
                  </span>
                  <span className="text-sm font-semibold text-text-primary flex-1 truncate">{s.title}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${CONF[s.confidence] || CONF.LOW}`}
                        title={s.confidence}>
                    {(() => {
                      const nl = t(`enum.confidence_pill.${s.confidence}`);
                      return nl && !nl.startsWith("enum.") ? nl : s.confidence;
                    })()}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-border-hairline p-4 space-y-3 bg-bg-panel" data-testid={`market-signal-detail-${i}`}>
                    <DetailRow label={t("market.what_happened")}
                               value={String(s.what_happened || "").replace(/^\s*(observation|waarneming)\s*:\s*/i, "")} />
                    <DetailRow label={t("market.why_matters")} value={s.why_it_matters_for_business} />
                    <DetailRow label={t("market.advice")} value={s.recommended_action} />
                    <div className="grid grid-cols-2 gap-3 pt-2 text-xs">
                      <div>
                        <span className="text-text-muted text-[10px]">{t("market.horizon")}</span>
                        <div className="text-text-primary">
                          {t(`market.timeframe.${s.time_horizon}`) && !t(`market.timeframe.${s.time_horizon}`).startsWith("market.")
                            ? t(`market.timeframe.${s.time_horizon}`)
                            : s.time_horizon}
                        </div>
                      </div>
                      <div>
                        <span className="text-text-muted text-[10px]">{t("market.relevance")}</span>
                        <div className="text-text-primary tabular-nums">{(s.business_relevance_score * 100).toFixed(0)}%</div>
                      </div>
                    </div>
                    {s.confidence_reason && (
                      <div className="text-[11px] text-text-muted italic pt-2">
                        {t("market.confidence")}: {s.confidence_reason}
                      </div>
                    )}
                    <div className="text-[10px] text-text-subtle flex items-center gap-2 flex-wrap">
                      <span>{t("market.source")}:</span>
                      {s.source_url ? (
                        <a href={s.source_url} target="_blank" rel="noreferrer"
                           data-testid={`market-signal-source-${i}`}
                           className="text-accent-teal hover:underline break-all">
                          {s.source_name || "Onbekende uitgever"}
                        </a>
                      ) : (
                        <span title={s.source_type === "llm_general_market_knowledge_fallback" || s.source_type === "llm_general_market_knowledge" ? t("market.source_llm_general_hint") : undefined}>
                          {s.source_name || (
                            s.source_type === "llm_general_market_knowledge_fallback" || s.source_type === "llm_general_market_knowledge"
                              ? t("market.source_llm_general")
                              : s.source_type
                          )}
                        </span>
                      )}
                      {s.pub_date && <span>· {s.pub_date}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-text-muted font-semibold mb-1">{label}</div>
      <div className="text-sm text-text-primary leading-relaxed">{value || "—"}</div>
    </div>
  );
}
